import { Checkbox } from "expo-checkbox";
import { LayoutChangeEvent, Platform, StyleSheet, View } from "react-native";
import { useTheme } from "@/hooks/useTheme";
import { TableHeaderCell } from "./TableCells/HeaderCell";
import { TableHeaderProps } from "./props";
import { LinearGradient } from "expo-linear-gradient";
import Animated, {
  useAnimatedReaction,
  useAnimatedRef,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  SharedValue,
  withSpring,
} from "react-native-reanimated";
import { useEffect, useRef, useState } from "react";
import _layout from "@/app/_layout";
import { Button } from "@/components/ui/Button";
import useContextMenu from "@/hooks/useContextMenu";
import { displayName } from "./types";
import { TableStart, TABLE_START_WIDTH_SELECTABLE, TABLE_START_WIDTH } from "./TableStart";
import { TableEnd } from "./TableEnd";

export function TableHeader({
  style,
  headerFunc = displayName,
  override,
  columnWidths,
  onSelectAll,
  anchorFields = [],
  scrollableFields = [],
  selectable,
  selected,
  columnClipping,
  resizeRealtime,
  scrollX,
  scrollYLayout,
  scrollYContentSize,
  setAnchorFields,
  columnPositionX,
  minCellWidth,
  loading,
  storageKey,
  query,
  data = [],
  currentPageData = [],
  headerActions,
  selectedRows = {},
  selectedRowIds = [],
  hasSummary = false,
  headerRightIcon = "download",
  headerRightAction,
  onMoveLeft,
  onMoveRight,
  orderby,
  onOrderByChange,
  onHideColumn,
}: TableHeaderProps) {
  const { colors } = useTheme();
  const contextMenu = useContextMenu();
  const ref = useAnimatedRef<Animated.ScrollView>();
  const buttonRef = useRef<View>(null);
  const [scrollXView, setScrollXView] = useState({ x: 0, y: 0, height: 0, width: 0 });
  const [contentSize, setContentSize] = useState({ w: 0, h: 0 });
  const [fadeHeight, setFadeHeight] = useState<number>(100);
  const [leftFadeVisible, setLeftFadeVisible] = useState(false);
  const [rightFadeVisible, setRightFadeVisible] = useState(false);
  const [resizeInfo, setResizeInfo] = useState<{
    width: SharedValue<number>;
    anchored: boolean;
    baseX: number;
  } | null>(null);
  const [dragInfo, setDragInfo] = useState<{
    columnKey: string;
    anchored: boolean;
    columnIndex: number;
    startX: number;
    currentX: number;
  } | null>(null);
  const AnimatedLinearGradient = Animated.createAnimatedComponent(LinearGradient);

  // Handle resize start from HeaderCell
  const handleResizeStart = (columnKey: string, width: SharedValue<number>, anchored: boolean, columnIndex: number) => {
    // Helper to check if a field is visible
    const isVisible = (field: string) => {
      const visibilityEntry = query?.visible?.find((item) => item.name === field);
      // If no entry exists, column is visible by default
      // If entry exists, check if visible !== false
      return !visibilityEntry || visibilityEntry.visible !== false;
    };

    // Calculate base X offset
    const startWidth = selectable ? TABLE_START_WIDTH_SELECTABLE : TABLE_START_WIDTH;
    let baseX = startWidth;

    if (anchored) {
      // Sum widths of VISIBLE anchor columns before this one
      for (let i = 0; i < columnIndex; i++) {
        const field = anchorFields[i];
        if (isVisible(field)) {
          baseX += columnWidths.value[field] || minCellWidth;
        }
      }
    } else {
      // Sum all VISIBLE anchor widths
      for (const field of anchorFields) {
        if (isVisible(field)) {
          baseX += columnWidths.value[field] || minCellWidth;
        }
      }
      // VISIBLE scrollable columns before this one
      const scrollableIndex = columnIndex - anchorFields.length;
      for (let i = 0; i < scrollableIndex; i++) {
        const field = scrollableFields[i];
        if (isVisible(field)) {
          baseX += columnWidths.value[field] || minCellWidth;
        }
      }
    }

    setResizeInfo({ width, anchored, baseX });
  };

  // Handle resize end
  const handleResizeEnd = () => {
    setResizeInfo(null);
  };

  // Helper to check if a field is visible
  const isFieldVisible = (field: string) => {
    const visibilityEntry = query?.visible?.find((item) => item.name === field);
    return !visibilityEntry || visibilityEntry.visible !== false;
  };

  // Helper to calculate drop index from X position
  const calculateDropIndex = (x: number, anchored: boolean): number => {
    const fields = anchored ? anchorFields : scrollableFields;
    const visibleFields = fields.filter(isFieldVisible);
    const startWidth = selectable ? TABLE_START_WIDTH_SELECTABLE : TABLE_START_WIDTH;

    let cumulative = startWidth;
    if (anchored) {
      // For anchored columns, just start from startWidth
    } else {
      // Add all visible anchor widths
      for (const f of anchorFields) {
        if (isFieldVisible(f)) {
          cumulative += columnWidths.value[f] || minCellWidth;
        }
      }
      // Subtract scroll offset for scrollable columns
      cumulative -= scrollX.value;
    }

    for (let i = 0; i < visibleFields.length; i++) {
      const w = columnWidths.value[visibleFields[i]] || minCellWidth;
      const midpoint = cumulative + w / 2;
      if (x < midpoint) return i;
      cumulative += w;
    }

    return visibleFields.length - 1;
  };

  // Handle drag start from HeaderCell
  const handleDragStart = (columnKey: string, anchored: boolean, columnIndex: number, startX: number) => {
    setDragInfo({ columnKey, anchored, columnIndex, startX, currentX: startX });
  };

  // Handle drag move
  const handleDragMove = (currentX: number) => {
    setDragInfo((prev) => (prev ? { ...prev, currentX } : null));
  };

  // Handle drag end
  const handleDragEnd = () => {
    if (!dragInfo) return;

    // Get visible fields for the current group
    const fields = dragInfo.anchored ? anchorFields : scrollableFields;
    const visibleFields = fields.filter(isFieldVisible);

    // Find current visible index
    const currentVisibleIndex = visibleFields.indexOf(dragInfo.columnKey);
    if (currentVisibleIndex === -1) {
      setDragInfo(null);
      return;
    }

    // Calculate target position based on currentX vs column boundaries
    const targetVisibleIndex = calculateDropIndex(dragInfo.currentX, dragInfo.anchored);

    // Move column to target position
    if (targetVisibleIndex !== currentVisibleIndex) {
      const delta = targetVisibleIndex - currentVisibleIndex;
      if (delta < 0) {
        for (let i = 0; i < Math.abs(delta); i++) onMoveLeft?.(dragInfo.columnKey);
      } else {
        for (let i = 0; i < delta; i++) onMoveRight?.(dragInfo.columnKey);
      }
    }

    setDragInfo(null);
  };

  // Overlay style that tracks the resizing column width and position
  const overlayStyle = useAnimatedStyle(() => {
    if (!resizeInfo) return { width: 0, left: 0 };

    let left = resizeInfo.baseX - 1; // -1 to account for border

    // For scrollable columns, subtract scroll offset
    if (!resizeInfo.anchored) {
      left -= scrollX.get();
    }

    return {
      width: resizeInfo.width.get(),
      left,
    };
  }, [resizeInfo]);

  // Drop indicator style that shows where the column will be inserted
  const dropIndicatorStyle = useAnimatedStyle(() => {
    if (!dragInfo) return { left: 0, opacity: 0 };

    const fields = dragInfo.anchored ? anchorFields : scrollableFields;
    const visibleFields = fields.filter(isFieldVisible);
    const targetIndex = calculateDropIndex(dragInfo.currentX, dragInfo.anchored);
    const startWidth = selectable ? TABLE_START_WIDTH_SELECTABLE : TABLE_START_WIDTH;

    let indicatorX = startWidth;

    if (dragInfo.anchored) {
      // For anchored columns
      for (let i = 0; i < targetIndex; i++) {
        indicatorX += columnWidths.value[visibleFields[i]] || minCellWidth;
      }
    } else {
      // Add all visible anchor widths
      for (const f of anchorFields) {
        if (isFieldVisible(f)) {
          indicatorX += columnWidths.value[f] || minCellWidth;
        }
      }
      // Add visible scrollable widths up to target
      for (let i = 0; i < targetIndex; i++) {
        indicatorX += columnWidths.value[visibleFields[i]] || minCellWidth;
      }
      // Subtract scroll offset for scrollable columns
      indicatorX -= scrollX.value;
    }

    return {
      left: withSpring(indicatorX - 1.5, { damping: 20, stiffness: 300 }),
      opacity: 1,
    };
  }, [dragInfo, anchorFields, scrollableFields, columnWidths, minCellWidth, selectable, scrollX]);

  const handleLayout = (event: LayoutChangeEvent) => {
    setScrollXView(event.nativeEvent.layout);
  };

  const handleContentSizeChange = (w: number, h: number) => {
    setContentSize({ w, h });
  };

  const transformHeader = (key: string): string => {
    if (override && key in override) {
      return override[key];
    }
    return headerFunc?.(key) ?? key;
  };

  const handleRightButtonClick = () => {
    if (headerRightAction && data && currentPageData) {
      headerRightAction(data, currentPageData);
    }
  };

  const scrollHandler = useAnimatedScrollHandler((event) => {
    const offsetX = event.contentOffset.x;
    const maxScrollX = event.contentSize.width - event.layoutMeasurement.width;
    scrollX.set(offsetX);
    // maxScroll.set(maxScrollX);
    setLeftFadeVisible(offsetX > 1);
    setRightFadeVisible(offsetX < maxScrollX - 1);
  });

  useAnimatedReaction(
    () => scrollX.value,
    (x) => {
      ref.current?.scrollTo({ x, animated: false });
    },
  );

  // useAnimatedReaction(
  //   () => showLeftFade.get(),
  //   (visible) => runOnJS(setLeftFadeVisible)(visible),
  //   [contentSize],
  // );

  // useAnimatedReaction(
  //   () => showRightFade.get(),
  //   (visible) => runOnJS(setRightFadeVisible)(visible),
  //   [contentSize],
  // );

  useEffect(() => {
    if (scrollYLayout?.height < scrollYContentSize.height) {
      setFadeHeight(scrollYLayout.height);
    } else {
      setFadeHeight(scrollYContentSize.height);
    }
  }, [scrollYContentSize, scrollYLayout]);

  useEffect(() => {
    if (scrollXView.width > contentSize.w) {
      setLeftFadeVisible(false);
      setRightFadeVisible(false);
    } else {
      setLeftFadeVisible(scrollX.value !== 0);
      setRightFadeVisible(scrollX.value < contentSize.w - scrollXView.width - 1);
    }
  }, [scrollXView.width, contentSize.w, scrollX.value]);

  // Calculate selection count
  const selectedCount = selectedRowIds.length;

  const menuContent = () =>
    headerActions?.map((action, i) => {
      const { onPress, ...buttonProps } = action;
      return (
        <Button
          key={i}
          {...buttonProps}
          style={{
            paddingHorizontal: 8,
            justifyContent: "space-between",
            borderWidth: 0,
            backgroundColor: "transparent",
            gap: 8,
          }}
          iconPosition="right"
          onPress={() => {
            if (onPress) {
              onPress({ selectedIds: selectedRowIds, selectedMap: selectedRows, count: selectedCount });
              contextMenu.setVisible(false);
            }
          }}
        />
      );
    });
  return (
    <Animated.View
      key={anchorFields.join(",") + "," + scrollableFields.join(",")}
      style={[styles.container, { backgroundColor: colors.card, borderColor: colors.border }, style]}
    >
      <TableStart selectable={selectable} backgroundColor={colors.card} borderColor={colors.border}>
        {selectable &&
          (Platform.OS === "web" ? (
            <input type="checkbox" onChange={onSelectAll} checked={selected} id={`check_header_${storageKey}`} />
          ) : (
            <Checkbox
              id={`check_header_${storageKey}`}
              style={styles.checkbox}
              value={selected}
              onValueChange={onSelectAll}
            />
          ))}
        {headerActions && (
          <Button
            ref={buttonRef}
            icon="dots-vertical"
            iconSize={18}
            style={styles.button}
            enabled={selectedCount > 0}
            onPress={() => {
              contextMenu.setMenuContent(menuContent);
              contextMenu.setIgnoreRef(buttonRef);
              buttonRef.current?.measureInWindow((x, y, _w, h) => {
                contextMenu.setLocation({ x, y: y + 8 + (h ?? 0) });
              });
              contextMenu.setVisible(!contextMenu.visible);
            }}
          />
        )}
      </TableStart>
      <Animated.View
        id={`h_${anchorFields.join(",")}`}
        style={[{ backgroundColor: colors.card, borderColor: colors.border }, styles.anchor]}
      >
        {anchorFields.map((key, i) => {
          if (query?.visible?.some((item) => item.name === key && item.visible === false)) return;
          return (
            <TableHeaderCell
              id={`th_${key}`}
              key={`th_${key}`}
              style={{ borderRightWidth: 1 }}
              relativeColumnIndex={i}
              columnIndex={i}
              text={transformHeader(key)}
              columnClipping={columnClipping}
              columnKey={key}
              columnWidths={columnWidths}
              resizeRealtime={resizeRealtime}
              columnPositionX={columnPositionX}
              anchored={true}
              anchoredCount={anchorFields.length}
              scrollableCount={scrollableFields.length}
              setAnchorFields={setAnchorFields}
              onResizeStart={handleResizeStart}
              onResizeEnd={handleResizeEnd}
              minCellWidth={minCellWidth}
              loading={loading}
              storageKey={storageKey}
              onMoveLeft={onMoveLeft}
              onMoveRight={onMoveRight}
              orderby={orderby}
              onOrderByChange={onOrderByChange}
              onHideColumn={onHideColumn}
              onDragStart={handleDragStart}
              onDragMove={handleDragMove}
              onDragEnd={handleDragEnd}
            />
          );
        })}
      </Animated.View>
      <Animated.View style={styles.gradientWrapper}>
        {leftFadeVisible && (
          <AnimatedLinearGradient
            colors={["rgba(0,0,0,0.45)", "transparent"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={[
              {
                position: "absolute",
                top: 0,
                height: 44 + fadeHeight + (hasSummary ? 48 : 0),
                width: 16,
                zIndex: 2,
                elevation: 2,
              },
            ]}
          />
        )}
      </Animated.View>
      <Animated.ScrollView
        ref={ref}
        id={`h_${scrollableFields.join(",")}`}
        key={`h_${scrollableFields.join(",")}`}
        horizontal={true}
        scrollEnabled={!loading}
        showsHorizontalScrollIndicator={false}
        onScroll={scrollHandler}
        onLayout={handleLayout}
        onContentSizeChange={handleContentSizeChange}
        scrollEventThrottle={16}
        style={[{ backgroundColor: colors.card, borderColor: colors.border }, styles.row]}
      >
        {scrollableFields.map((key, i) => {
          if (query?.visible?.some((item) => item.name === key && item.visible === false)) return;
          return (
            <TableHeaderCell
              id={`th_${key}`}
              key={`th_${key}`}
              style={{ borderRightWidth: 1 }}
              relativeColumnIndex={i}
              columnIndex={anchorFields.length + i}
              text={transformHeader(key)}
              columnClipping={columnClipping}
              columnPositionX={columnPositionX}
              columnKey={key}
              columnWidths={columnWidths}
              resizeRealtime={resizeRealtime}
              anchored={false}
              anchoredCount={anchorFields.length}
              scrollableCount={scrollableFields.length}
              setAnchorFields={setAnchorFields}
              onResizeStart={handleResizeStart}
              onResizeEnd={handleResizeEnd}
              minCellWidth={minCellWidth}
              loading={loading}
              storageKey={storageKey}
              onMoveLeft={onMoveLeft}
              onMoveRight={onMoveRight}
              orderby={orderby}
              onOrderByChange={onOrderByChange}
              onHideColumn={onHideColumn}
              onDragStart={handleDragStart}
              onDragMove={handleDragMove}
              onDragEnd={handleDragEnd}
            />
          );
        })}
      </Animated.ScrollView>
      <Animated.View style={styles.gradientWrapper}>
        {rightFadeVisible && (
          <AnimatedLinearGradient
            colors={["rgba(0,0,0,0.45)", "transparent"]}
            start={{ x: 1, y: 0 }}
            end={{ x: 0, y: 0 }}
            style={[
              {
                position: "absolute",
                top: 0,
                right: 0,
                height: 44 + fadeHeight + (hasSummary ? 48 : 0),
                width: 16,
                zIndex: 2,
                elevation: 2,
              },
            ]}
          />
        )}
      </Animated.View>
      {/* Resize overlay */}
      {resizeInfo && (
        <Animated.View
          pointerEvents="none"
          style={[
            {
              position: "absolute",
              top: 0,
              height: 44 + fadeHeight + (hasSummary ? 48 : 0),
              backgroundColor: "rgba(0, 120, 255, 0.08)",
              borderRightWidth: 2,
              borderRightColor: colors.primary,
              zIndex: 100,
            },
            overlayStyle,
          ]}
        />
      )}
      {/* Drop indicator for column reordering */}
      {dragInfo && (
        <Animated.View
          pointerEvents="none"
          style={[
            {
              position: "absolute",
              top: 0,
              height: 44 + fadeHeight + (hasSummary ? 48 : 0),
              width: 3,
              backgroundColor: colors.primary,
              borderRadius: 1.5,
              zIndex: 101,
            },
            dropIndicatorStyle,
          ]}
        />
      )}
      <TableEnd borderColor={colors.border}>
        <Button
          enabled={!loading && !!headerRightAction}
          style={styles.button}
          icon={headerRightIcon}
          iconSize={16}
          onPress={handleRightButtonClick}
        />
      </TableEnd>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 6,
    borderWidth: StyleSheet.hairlineWidth,
    zIndex: 2,
    elevation: 2,
    flexDirection: "row",
    alignSelf: "flex-start",
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: "auto",
    height: 38,
    maxWidth: "100%",
    width: "100%",
  },
  anchor: {
    minHeight: 32,
    flexDirection: "row",
  },
  row: {
    minHeight: 32,
    flexDirection: "row",
  },
  gradientWrapper: {
    position: "relative",
    zIndex: 2,
    elevation: 2,
  },
  checkbox: {
    zIndex: 2,
    elevation: 2,
  },
  button: {
    height: 18,
    width: 18,
    backgroundColor: "transparent",
    borderWidth: 0,
  },
});
